import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import { BiSolidBot } from "react-icons/bi";
import { Link } from "react-router-dom";
import CustomerSupportIcon from "../helper/images/customer.png";
import { useAuth } from "../context/AuthContext";
import UserImage from "../helper/images/customer.png";
import socket from "../helper/socket";
import { FaCheck, FaCheckDouble } from "react-icons/fa";
import { useTranslation } from "../hooks/useTranslation";

function Chat({
  selectedChat,
  messages,
  inputMessage,
  setInputMessage,
  messageMutation,
  enabled,
  userData,
  totalParticipants,
  handleSwitchToggle,
}) {
  const messagesEndRef = useRef(null);
  const [isOwnMessageSent, setIsOwnMessageSent] = useState(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const { t } = useTranslation();



  return (
    <div className="w-full rounded-[20px] sm:p-[30px] p-4">
      <div className="flex items-center pb-[30px] border-b border-[#EEEEEE] gap-[14px]">
        <div className="w-[57px] h-[57px] bg-[#FFF3EF] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
          <BiSolidBot size={30} className="text-black" />
        </div>
        <h2 className="text-[22px] font-medium">{t("SupportChat").support}</h2>

        {handleSwitchToggle && (
          <div className="flex items-center gap-[13px]">
            <div
              onClick={() =>
                handleSwitchToggle(
                  selectedChat?.contextInfo?.restaurantId?.ownerId ||
                    selectedChat?.contextInfo?.hotelId?.ownerId
                )
              }
              className={`w-[50px] h-[24px] flex items-center rounded-full p-1 cursor-pointer transition-colors break-words  ${
                enabled ? "bg-blue-500" : "bg-[#e1e1e1]"
              }`}
            >
              <div
                className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                  enabled ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </div>
            <h3 className="text-lg font-semibold">
              {t("SupportChat").switchChatToOwner}
            </h3>
          </div>
        )}
      </div>
      <div className="h-[350px] overflow-y-auto pt-[17px] pb-[38px]">
        <h2 className="text-[22px] font-medium mb-[15px]">
          {t("SupportChat").userDetails}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr] md:gap-[46px] gap-[10px] mb-[23px] items-start">
          {/* user name and image  */}
          <div className="flex items-center gap-[9px]">
            <img
              src={
                selectedChat?.createdBy?.profilePicture !== "" ? selectedChat?.createdBy?.profilePicture  : CustomerSupportIcon
              }
              alt="Customer Support"
              className="w-[43px] h-[43px] rounded-[50%] "
            />
            <div>
              <h3 className="text-base font-medium mb-1 leading-[19px]">
                {selectedChat?.createdBy?.name}
              </h3>
              <p className="text-sm text-[#484848]">
                {selectedChat?.createdBy?.email}
              </p>
            </div>
          </div>
          {/* user details */}
          <div>
            <div className="flex flex-col  gap-[13px]">
              <p className="text-sm text-[#18181B]">
                <span className="font-medium">
                  {t("SupportChat").requestType}:
                </span>
                {selectedChat?.reason}
              </p>
              <p className="text-sm text-[#18181B]">
                <span className="font-medium">
                  {t("SupportChat").requestDate}:{" "}
                </span>
                {new Date(selectedChat?.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          <div>
            <div className="flex flex-col  gap-[13px]">
              {selectedChat?.contextType === "order" ||
              selectedChat?.contextType === "table" ? (
                <>
                  <p className="text-sm text-[#18181B]">
                    <span className="font-medium">
                      {t("SupportChat").restaurantName}:{" "}
                    </span>
                    {selectedChat?.contextInfo?.restaurantId?.name}
                  </p>
                  <p className="text-sm text-[#18181B]">
                    <span className="font-medium">
                      {" "}
                      {t("SupportChat").bookingId}:{" "}
                    </span>
                    #
                    {selectedChat?.contextInfo?.bookingId ||
                      selectedChat?.contextInfo?.orderId ||
                      selectedChat?.contextInfo?.reservationId ||
                      "N/A"}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#18181B]">
                    <span className="font-medium">: </span>
                    {selectedChat?.contextInfo?.hotelId?.name}
                  </p>
                  <p className="text-sm text-[#18181B]">
                    <span className="font-medium">
                      {t("SupportChat").bookingId}:{" "}
                    </span>
                    #
                    {selectedChat?.contextInfo?.bookingId ||
                      selectedChat?.contextInfo?.orderId ||
                      selectedChat?.contextInfo?.reservationId ||
                      "N/A"}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <hr className="my-4 border-t-4 border-gray-200" />

        <div className="flex flex-col gap-[20px] mt-6">
          {messages?.map((msg) => {
            const isSentByCurrentUser =
              msg.senderId?._id === userData?.id ||
              msg.senderId === userData?.id;

            const readCount = msg.readBy?.length || 0;
            const othersReadCount =
              msg.readBy?.filter((id) => id !== userData?.id)?.length || 0;
            const isReadByAny = othersReadCount > 0;
            const isReadByAll = othersReadCount === totalParticipants - 1;

            return (
              <div
                key={msg._id}
                className={`flex gap-[20px] ${
                  isSentByCurrentUser ? "justify-end" : ""
                }`}
              >
                {!isSentByCurrentUser && (
                  <img
                    src={
                      msg.senderId?.profilePicture
                        ? `${msg.senderId.profilePicture}`
                        : UserImage
                    }
                    alt="user"
                    className="w-[43px] h-[43px] rounded-full"
                  />
                )}

                <div
                  className={`py-[13px] px-[17px] border border-[#D9D9D9] rounded-[10px]  ${
                    isSentByCurrentUser ? "bg-[#FFF3EF] text-right" : ""
                  } max-w-[497px]`}
                >
                  <p className="text-base leading-[26px] text-black break-words">
                    {msg.content}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <p className="text-xs text-gray-400  mt-[4px]">
                      {new Date(msg.createdAt).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                    {/* {isSentByCurrentUser && (
                      <div className="flex items-center justify-end mt-1 gap-0">
                        <span
                          className={`${isReadByAll ? "text-blue-500" : "text-gray-400"
                            } text-[15px]`}
                        >
                          {isReadByAll || isReadByAny ? "✓✓" : "✓"}
                        </span>
                      </div>
                    )} */}

                    {isSentByCurrentUser && (
                      <span
                        className={`ml-1 ${
                          isReadByAll ? "text-blue-500" : "text-gray-400"
                        }`}
                      >
                        {isReadByAll ? (
                          <FaCheckDouble size={14} />
                        ) : isReadByAny ? (
                          <FaCheckDouble size={14} />
                        ) : (
                          <FaCheck size={14} />
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {isSentByCurrentUser && (
                  <div className="w-[43px] h-[43px] bg-[#FFF3EF] rounded-full flex items-center justify-center hover:bg-[#FF9D00]">
                    <img
                      src={
                        msg.senderId?.profilePicture
                          ? `${msg.senderId.profilePicture}`
                          : UserImage
                      }
                      alt="user"
                      className="w-[43px] h-[43px] rounded-full"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div ref={messagesEndRef} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-[16px] pt-[26px] border-t border-[#EEEEEE]">
        <input
          type="text"
          placeholder={t("SupportChat").typeHere}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              inputMessage.trim() &&
              !messageMutation.isLoading
            ) {
              messageMutation.mutate({
                chatId: selectedChat._id,
                content: inputMessage,
              });
              setIsOwnMessageSent(true);
            }
          }}
          className="p-4 bg-white border border-[#D9D9D9] rounded-[10px] w-full focus:outline-none focus:border-[#4CAF50]"
        />
        <button
          onClick={() => {
            messageMutation.mutate({
              chatId: selectedChat._id,
              content: inputMessage,
            });

            setIsOwnMessageSent(true);
          }}
          disabled={messageMutation.isLoading || !inputMessage.trim()}
          className="inline-block rounded-[2px] bg-[#4CAF50] text-base text-white font-bold py-[14px] px-[38px] hover:bg-[#209b25]"
        >
          {messageMutation.isLoading
            ? t("SupportChat").sending
            : t("SupportChat").sendMessage}
        </button>
      </div>
    </div>
  );
}

export default Chat;
